import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Collapse, Stack} from '@mui/material';
import {getSourcePackageSummary} from '../../../utils/api/sourcePackage';
import {toBackendIcr} from '../../../utils/formatIcrDescription';
import SummaryHeader from './SummaryHeader';
import SummaryContent from './SummaryContent';
import {useReportStore} from '../../../features/products/useReportStore';

/**
 * SourcePackageSummary - Collapsible summary of source package against ICR.
 * Only displayed when ICR exists for the product.
 */
const SourcePackageSummary = ({reportId, icr, userId, items = []}) => {
    const [expanded, setExpanded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [summary, setSummary] = useState(null);

    const segmentIds = useMemo(() => {
        const ids = new Set();

        items.forEach((item) => {
            if (item?.segmentId === null || item?.segmentId === undefined || item.segmentId === '') {
                return;
            }
            ids.add(String(item.segmentId));
        });

        return Array.from(ids);
    }, [items]);

    const segmentIdsSignature = useMemo(() => segmentIds.join(','), [segmentIds]);

    useEffect(() => {
        setSummary(null);
        setError(null);
    }, [segmentIdsSignature]);

    const fetchSummary = useCallback(async () => {
        if (summary || loading) return;

        setLoading(true);
        setError(null);

        try {
            const backendIcr = toBackendIcr(icr);
            const result = await getSourcePackageSummary(reportId, backendIcr, userId, segmentIds);
            setSummary(result);
        } catch (err) {
            console.error('Failed to fetch source package summary:', err);
            setError(err.message || 'Failed to load summary');
        } finally {
            setLoading(false);
        }
    }, [reportId, icr, userId, summary, loading, segmentIds]);

    const handleToggle = () => {
        const willExpand = !expanded;
        setExpanded(willExpand);
        if (willExpand) {
            fetchSummary();
        }
    };

    const handleTopicClick = (topic) => {
        useReportStore.getState().setLeftPanelTab('search');
        useReportStore.getState().setQuery(topic);
        useReportStore.getState().setAiSearch(true);
    };

    return (
        <Stack sx={{bgcolor: 'white'}}>
            <SummaryHeader expanded={expanded} onToggle={handleToggle}/>
            <Collapse in={expanded}>
                <SummaryContent
                    loading={loading}
                    error={error}
                    summary={summary}
                    onTopicClick={handleTopicClick}
                />
            </Collapse>
        </Stack>
    );
};

export default SourcePackageSummary;
